/**
 * Turning an opaque media key into something an `<img>` or `<video>` can load.
 *
 * A device hands us a storage key, never a URL — a header-less media element cannot carry the
 * operator's JWT, so the key is POSTed to a resolver that returns a short-lived presigned URL.
 *
 * There are TWO resolvers and this module picks between them by FOLDER. That is the security
 * property, stated at `robot-media-resolve.api.ts`: each resolver accepts only its own folder, so
 * a helmet capture cannot be laundered through the snapshot resolver and a snapshot cannot be
 * laundered through the helmet one. Sending a key to the wrong resolver does not silently widen
 * anything — the backend refuses it — but it does turn a legitimate thumbnail into a dead one,
 * which is how a helmet's alarm photo would quietly stop rendering.
 *
 * Most alert-log photos never reach a resolver at all. The log asks `GET /robots/alerts` to sign the
 * photos it lists (`include=mediaUrls`, through the same per-folder gates) and hands the links to
 * `primeMediaUrls` before its rows render, so a thumbnail finds its link already cached. The 5 s
 * open-alerts poll does not ask (see `useRobotAlertsPolling`). The resolvers remain the fallback: for
 * a photo the list did not sign, a lapsed link, or a key that reached the console some other way (a
 * live push, the emergency card, the media list, the face gallery).
 */

import { isTransientResolveError } from './media-resolve-batcher'
import { __resetMediaBatchForTests, requestHelmetUrl, requestSnapshotUrl } from './robot-media-batch'

export { isTransientResolveError, MAX_RESOLVE_ATTEMPTS } from './media-resolve-batcher'

/** Which resolver owns a key. */
export type MediaRefKind = 'snapshot' | 'helmet'

/**
 * Classify a media key, or null if it is not one.
 *
 * The client-side shape check is a pre-filter, not the guard: it rejects `javascript:`, `data:`,
 * absolute URLs and anything else that is not a bare `<folder>/…` path, so a compromised device
 * payload is never even attempted as a `src`. The authoritative check is the backend's per-tenant
 * (and, for a helmet, per-device) regex.
 */
export function classifyMediaRef(ref: string | undefined): MediaRefKind | null {
  if (!ref) return null

  // Any `<scheme>:` prefix — and in fact any colon at all — disqualifies it. A valid key has none.
  if (ref.includes(':')) return null

  if (ref.startsWith('robots/')) return 'snapshot'
  if (ref.startsWith('helmets/')) return 'helmet'

  return null
}

/**
 * How long a resolved URL is reused. Well UNDER the server presign TTL (1 h for both resolvers): a
 * gallery left open past the expiry would otherwise keep serving a URL S3 has stopped honouring.
 */
export const CLIENT_TTL_MS = 45 * 60 * 1000

/**
 * When a newer listing's link replaces a cached one. Until then a fresh signature is ignored: every
 * new signature is a new `<img src>`, so swapping it on each 5 s poll would re-download every photo
 * and flicker. After it, a tile mounting now gets a link with at least a quarter of an hour left
 * rather than one about to lapse.
 */
export const PRIME_REFRESH_AGE_MS = 30 * 60 * 1000

/**
 * How long a DEFINITIVE refusal (400/403/404) is remembered — long enough that a dead key cannot
 * loop, short enough that a fixed one comes back without a reload.
 */
export const NEGATIVE_TTL_MS = 60 * 1000

/**
 * How long a TRANSIENT failure is remembered once the batcher's retries are spent.
 *
 * Remembering nothing meant every remount and every dashboard refresh tick fired the same failing
 * resolve again — against a backend that was failing because it was overloaded, an alert log of
 * fifty photos kept it that way. Short enough that a recovered backend is picked up without a
 * reload; `evictMediaRef` still forces a fresh try at any time.
 */
export const TRANSIENT_COOLDOWN_MS = 30 * 1000

interface CacheEntry {
  promise: Promise<string | null>
  at: number
  ttl: number
  /** A resolve still in flight, a working link, or a remembered failure. */
  state: 'pending' | 'ok' | 'failed'
}

interface Outcome {
  url: string | null
  ttl: number
}

const cache = new Map<string, CacheEntry>()

/** Orders prime tickets against evictions; see `beginMediaPrime`. */
let clock = 0
/** Per cache key, its latest eviction: the clock reading, and the time (for pruning). */
const evictedAt = new Map<string, { seq: number; at: number }>()

/**
 * The key a link is cached under.
 *
 * `robotId` narrows a helmet resolve, so it must be in a helmet key. The snapshot resolver takes no
 * device (a snapshot belongs to the tenant), so a snapshot key leaves it out: the alert log, the
 * emergency card and the enrol dialog pass different robotIds, or none, for the same photo, and must
 * share one link.
 */
function cacheKey(kind: MediaRefKind, ref: string, robotId?: string): string {
  return kind === 'snapshot' ? `${ref}|` : `${ref}|${robotId ?? ''}`
}

/**
 * Both kinds go through a batcher, which also owns the retries: a failed batch is retried as ONE
 * request per round, not one per tile, and what reaches this module is the final outcome.
 */
function fetchUrl(kind: MediaRefKind, ref: string, robotId?: string): Promise<string | null> {
  if (kind === 'helmet') return requestHelmetUrl(ref, robotId)

  return requestSnapshotUrl(ref)
}

async function attemptResolve(fetchOnce: () => Promise<string | null>): Promise<Outcome> {
  try {
    const url = await fetchOnce()

    return url ? { url, ttl: CLIENT_TTL_MS } : { url: null, ttl: NEGATIVE_TTL_MS }
  } catch (err) {
    // Retries are already spent. A transient failure is held only for the cooldown, so the next
    // remount or refresh tick does not fire it again straight away, and a recovered backend is
    // asked again soon after; a definitive refusal is held for the negative TTL.
    return { url: null, ttl: isTransientResolveError(err) ? TRANSIENT_COOLDOWN_MS : NEGATIVE_TTL_MS }
  }
}

/**
 * Resolve one media key to a presigned URL, or null if it cannot be resolved.
 *
 * Never throws: a failed resolve renders a placeholder, which is the correct outcome for a tile.
 * Concurrent callers for one key share a single request. Only a SUCCESS is kept for long; a refusal
 * is held for `NEGATIVE_TTL_MS` and a transient failure for `TRANSIENT_COOLDOWN_MS`.
 */
export function resolveMediaUrl(ref: string, robotId?: string): Promise<string | null> {
  const kind = classifyMediaRef(ref)

  if (!kind) return Promise.resolve(null)

  const key = cacheKey(kind, ref, robotId)
  const cached = cache.get(key)

  if (cached && Date.now() - cached.at < cached.ttl) return cached.promise

  // Held at the success TTL while in flight, so every tile asking meanwhile joins this request.
  const entry: CacheEntry = { promise: Promise.resolve(null), at: Date.now(), ttl: CLIENT_TTL_MS, state: 'pending' }

  entry.promise = attemptResolve(() => fetchUrl(kind, ref, robotId)).then(outcome => {
    // Only touch the entry this request owns — an eviction may already have replaced it.
    if (cache.get(key) === entry) {
      entry.at = Date.now()
      entry.ttl = outcome.ttl
      entry.state = outcome.url ? 'ok' : 'failed'
    }

    return outcome.url
  })

  cache.set(key, entry)

  return entry.promise
}

/**
 * Drop a cached resolve — used to self-heal a URL that has lapsed mid-display, and by a failed tile
 * asking again on its own. It clears a remembered failure too, so the next ask is a fresh request.
 * A listing that was already on its way when this ran cannot put a link back (see `primeMediaUrls`).
 */
export function evictMediaRef(ref: string, robotId?: string): void {
  const kind = classifyMediaRef(ref)

  if (!kind) return

  const key = cacheKey(kind, ref, robotId)

  cache.delete(key)
  evictedAt.set(key, { seq: ++clock, at: Date.now() })
}

/** Taken just before a listing is requested; `primeMediaUrls` uses it to date that listing's links. */
export interface MediaPrimeTicket {
  seq: number
  at: number
}

export function beginMediaPrime(): MediaPrimeTicket {
  return { seq: ++clock, at: Date.now() }
}

/**
 * Drop what can no longer matter, so a console left open all day does not keep every link it ever
 * saw: a settled entry past its TTL (a read would ask again anyway), and an eviction older than a
 * link's whole client life. An eviction only stops a listing requested before it, and such a
 * listing is older still, so `primeMediaUrls` writes nothing from it.
 */
function pruneExpired(now: number): void {
  for (const [key, entry] of cache) {
    if (entry.state !== 'pending' && now - entry.at >= entry.ttl) cache.delete(key)
  }

  for (const [key, eviction] of evictedAt) {
    if (now - eviction.at >= CLIENT_TTL_MS) evictedAt.delete(key)
  }
}

/** `helmets/<tenant>/<device>/…` → the device, which the server checked against the alert's own. */
function helmetDevice(ref: string): string | undefined {
  const device = ref.split('/')[2]

  return device && /^[a-f0-9]{24}$/.test(device) ? device : undefined
}

/**
 * Cache links a listing already signed (`GET /robots/alerts` → `mediaUrls`), so nothing has to
 * resolve them.
 *
 * A helmet link is cached for the device named in its own key — the device the server required to
 * match the alert carrying it, and the one every viewer of that alert asks with. A snapshot link
 * serves every caller of its key.
 *
 * A link is written only where there is nothing better: no entry, a remembered failure, or a link at
 * least `PRIME_REFRESH_AGE_MS` old. A resolve in flight is left to finish, and a key evicted after
 * the ticket was taken is skipped: that eviction is newer than this listing, so the self-heal it
 * started must not be overridden by a link signed before it. A primed link's age counts from the
 * ticket — before the server signed it — so the client TTL always ends inside the server's; a listing
 * requested a whole `CLIENT_TTL_MS` ago therefore has nothing left to cache. Each call also prunes
 * expired entries (`pruneExpired`).
 */
export function primeMediaUrls(urls: unknown, ticket: MediaPrimeTicket): void {
  if (!urls || typeof urls !== 'object') return

  const now = Date.now()

  pruneExpired(now)
  if (now - ticket.at >= CLIENT_TTL_MS) return

  for (const [ref, url] of Object.entries(urls)) {
    const kind = classifyMediaRef(ref)

    if (!kind || typeof url !== 'string' || !/^https?:\/\//i.test(url)) continue

    const device = kind === 'helmet' ? helmetDevice(ref) : undefined

    if (kind === 'helmet' && !device) continue

    const key = cacheKey(kind, ref, device)
    const held = cache.get(key)

    if ((evictedAt.get(key)?.seq ?? 0) > ticket.seq) continue
    if (held?.state === 'pending') continue
    if (held?.state === 'ok' && now - held.at < PRIME_REFRESH_AGE_MS) continue

    cache.set(key, { promise: Promise.resolve(url), at: ticket.at, ttl: CLIENT_TTL_MS, state: 'ok' })
  }
}

/** Test-only: how many links and evictions the module holds, to see what pruning dropped. */
export function __mediaRefCacheSizesForTests(): { links: number; evictions: number } {
  return { links: cache.size, evictions: evictedAt.size }
}

/** Test-only: clear the module-level cache (and cancel any sleeping retry) between specs. */
export function __resetMediaRefCacheForTests(): void {
  cache.clear()
  evictedAt.clear()
  __resetMediaBatchForTests()
}
